import React, { useState } from "react";
import { Stack, Box } from "@mui/material";
import { Link } from "react-router-dom";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import HeaderContainer from "styles/components/headerContainer";
import ListComponent from "components/CBList";
import theme from "assets/theme";
import useMediaQueries from "utils/mediaQueries.utils";
import StateSelector from "components/StateSelector";
import styled from "@emotion/styled";
import { StyledButtonContainer } from "index.styles";
import { Phone } from "@mui/icons-material";

const ReadMoreButton = styled.button`
  color: ${theme.palette.accent.main};
  cursor: pointer;
  display: inline;
  font-weight: 800;
  margin-left: 4px;
  background: none;
  border: none;
  padding: 0;
  font: inherit;

  &:hover {
    text-decoration: underline;
    opacity: 0.8;
  }

  &:focus {
    outline: 2px solid ${theme.palette.primary.main};
    outline-offset: 2px;
  }

  &:active {
    opacity: 0.6;
  }
`;

const iconMapping = {
  PhoneIcon: Phone,
};

const TextBlock = ({
  title,
  titleH1,
  titleH2,
  titleH3,
  description,
  collapsible = false,
  cta,
  ctaButtons,
  primaryCta,
  secondaryCta,
  ctaSubtext,
  listItems,
  listType = "bullet",
  highlightText,
  highlightColor = theme.palette.accent.main,
  textColor = theme.palette.primary.main,
  spacing = 2,
  component = "h2",
  variant = "h2",
  stateSelector,
  cssProps,
  textBlockCssProps,
  stackCssProps,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const descriptionArray = Array.isArray(description) ? description : [description];
  const { isLg } = useMediaQueries();

  const renderSegments = (segments) =>
    segments.map((seg, i) => {
      if (seg.link) {
        return (
          <Link
            key={i}
            to={seg.link}
            target={seg.external ? "_blank" : undefined}
            rel={seg.external ? "noopener noreferrer" : undefined}
            style={{ color: theme.palette.accent.main, fontWeight: 700, textDecoration: "none" }}
          >
            {seg.text}
          </Link>
        );
      }
      return <React.Fragment key={i}>{seg.text}</React.Fragment>;
    });

  // Function to truncate text to approximately 2-3 lines
  const truncateText = (text, maxChars = isLg ? 200 : 150) => {
    if (!text || text.length <= maxChars) return text;

    // Find the last space before the character limit to avoid cutting words
    const truncated = text.substring(0, maxChars);
    const lastSpace = truncated.lastIndexOf(" ");
    return lastSpace > 0 ? truncated.substring(0, lastSpace) + "..." : truncated + "...";
  };

  // Handle different CTA configurations
  let buttons = [];
  if (primaryCta && secondaryCta) {
    buttons = [primaryCta, secondaryCta];
  } else if (primaryCta) {
    buttons = [primaryCta];
  } else if (ctaButtons) {
    buttons = ctaButtons;
  } else if (cta) {
    buttons = [cta];
  }

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const renderDescription = () => {
    if (!description) return null;

    if (collapsible) {
      // For collapsible descriptions, concatenate all paragraphs into one.
      // Segment-style items aren't truncatable, so fall back to plain rendering if any item isn't a string.
      const allStrings = descriptionArray.every((item) => typeof item === "string");
      if (!allStrings) {
        return descriptionArray.map((item, index) =>
          typeof item === "string" ? (
            <MKTypography variant={isLg ? "body1" : "body2"} element="p" key={index} sx={{ color: `${textColor} !important`, ...textBlockCssProps }}>
              {item}
            </MKTypography>
          ) : (
            <MKTypography variant={isLg ? "body1" : "body2"} element="p" key={index} sx={{ color: `${textColor} !important`, ...textBlockCssProps }}>
              {renderSegments(item.segments)}
            </MKTypography>
          ),
        );
      }
      const fullText = descriptionArray.join(" ");
      const shouldTruncate = fullText.length > (isLg ? 200 : 150);

      if (!shouldTruncate) {
        // If text is short enough, just render normally
        return descriptionArray.map((item, index) => (
          <MKTypography variant={isLg ? "body1" : "body2"} element="p" key={index} sx={{ color: `${textColor} !important`, ...textBlockCssProps }}>
            {item}
          </MKTypography>
        ));
      }

      return (
        <MKTypography variant="body2" element="p" sx={{ color: `${textColor} !important`, ...textBlockCssProps }}>
          {isExpanded ? fullText : truncateText(fullText)}
          <ReadMoreButton onClick={handleToggleExpanded} aria-expanded={isExpanded} aria-label={isExpanded ? "Read less" : "Read more"}>
            {isExpanded ? " Read Less" : " Read More"}
          </ReadMoreButton>
        </MKTypography>
      );
    }

    // Non-collapsible descriptions render as separate paragraphs
    return descriptionArray.map((item, index) => (
      <MKTypography variant={isLg ? "body1" : "body2"} element="p" key={index} sx={{ color: `${textColor} !important`, ...textBlockCssProps }}>
        {typeof item === "string" ? item : renderSegments(item.segments)}
      </MKTypography>
    ));
  };

  return (
    <Stack direction="column" spacing={spacing} sx={{ margin: "0rem !important" }}>
      {/* Title rendering */}
      {titleH1 && (
        <HeaderContainer
          cssProps={cssProps}
          title={titleH1}
          textColor={textColor}
          component="h1"
          variant="h1"
          highlightText={highlightText}
          highlightColor={highlightColor}
        />
      )}

      {titleH2 && (
        <HeaderContainer
          cssProps={cssProps}
          title={titleH2}
          textColor={textColor}
          component="h2"
          variant="h2"
          highlightText={highlightText}
          highlightColor={highlightColor}
        />
      )}

      {titleH3 && (
        <HeaderContainer
          cssProps={cssProps}
          title={titleH3}
          textColor={textColor}
          component="h3"
          variant={"h3"}
          highlightText={highlightText}
          highlightColor={highlightColor}
        />
      )}

      {title && (
        <HeaderContainer
          cssProps={cssProps}
          title={title}
          textColor={textColor}
          component="h2"
          variant="h2"
          highlightText={highlightText}
          highlightColor={highlightColor}
        />
      )}

      {/* Description paragraphs with collapsible functionality */}
      {renderDescription()}

      {/* List Items */}
      {listItems && listItems.length > 0 && (
        <Box sx={{ margin: "1rem 0" }}>
          <ListComponent listItems={listItems} listType={listType === "bullet" ? undefined : listType} textColor={textColor} />
        </Box>
      )}

      {/* Enhanced CTA Buttons */}
      {buttons.length > 0 && (
        <StyledButtonContainer sx={{ display: "flex", gap: "1rem", flexDirection: isLg ? "row" : "column" }}>
          {buttons.map((button, index) => (
            <MKButton
              key={index}
              variant={index === 0 ? "contained" : "outlined"}
              color="secondary"
              size="large"
              sx={{ padding: "0rem 2rem", ...button.cssButtonProps }}
              component={Link}
              to={button.link}
              aria-label={button.ariaLabel && button.ariaLabel}
              target={button.external ? "_blank" : undefined}
              rel={button.external ? "noopener noreferrer" : undefined}
              startIcon={
                button.startIcon &&
                iconMapping[button.startIcon.iconName] &&
                React.createElement(iconMapping[button.startIcon.iconName], { sx: { fontSize: "1.25rem", color: "inherit !important" } })
              }
            >
              {button.text}
            </MKButton>
          ))}

          {/* CTA Subtext */}
          {ctaSubtext && (
            <MKTypography
              variant="body2"
              component="p"
              sx={{
                marginTop: "1rem",
                color: theme.palette.accent.main,
                fontStyle: "italic",
              }}
            >
              {ctaSubtext}
            </MKTypography>
          )}
        </StyledButtonContainer>
      )}

      {/* State Selector */}
      {stateSelector && <StateSelector listOfStates={stateSelector.listOfStates} destinationUrl={stateSelector.destinationUrl} />}
    </Stack>
  );
};

export default TextBlock;
